import { defineStore } from 'pinia'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { ref } from 'vue'
import { storeRegistry } from '@/store/storeRegistry'

// Extend dayjs with UTC plugin
dayjs.extend(utc)

export const useDateRangeStore = defineStore('dateRange', () => {
  // Default to last 60 days in local time with UTC offset
  // Set default start date to 60 days ago at 12:00:00 AM local time
  const defaultStartDate = dayjs().subtract(60, 'days').startOf('day')
  // Set default end date to today at 11:59:59 PM local time
  const defaultEndDate = dayjs().endOf('day')

  // Store the date range as ISO strings with local UTC offset for consistency
  const startDate = ref<string>(defaultStartDate.format('YYYY-MM-DDTHH:mm:ssZ'))
  const endDate = ref<string>(defaultEndDate.format('YYYY-MM-DDTHH:mm:ssZ'))

  // Whether the date range is active/being used
  const isActive = ref<boolean>(true)

  // Reset to default values (60 days)
  function resetDateRange() {
    const newDefaultStartDate = dayjs().subtract(60, 'days').startOf('day')
    const newDefaultEndDate = dayjs().endOf('day')
    startDate.value = newDefaultStartDate.format('YYYY-MM-DDTHH:mm:ssZ')
    endDate.value = newDefaultEndDate.format('YYYY-MM-DDTHH:mm:ssZ')
    isActive.value = true
  }

  // Set new date range
  function setDateRange(start: string, end: string) {
    startDate.value = start
    endDate.value = end
    isActive.value = true
  }

  // Toggle date range active state
  function toggleActive(active?: boolean) {
    if (active !== undefined) {
      isActive.value = active
    } else {
      isActive.value = !isActive.value
    }
  }

  // Get formatted dates for API calls (local time with UTC offset)
  function getFormattedDates() {
    if (!isActive.value) {
      return { startDate: undefined, endDate: undefined }
    }
    
    return {
      startDate: startDate.value,
      endDate: endDate.value
    }
  }

  // Set date range to last 30 days
  function set30DayRange() {
    // Set start date to 30 days ago at 12:00:00 AM local time
    const start = dayjs().subtract(30, 'days').startOf('day')
    // Set end date to today at 11:59:59 PM local time
    const end = dayjs().endOf('day')
    
    startDate.value = start.format('YYYY-MM-DDTHH:mm:ssZ')
    endDate.value = end.format('YYYY-MM-DDTHH:mm:ssZ')
    isActive.value = true
  }

  // Set date range to last 60 days
  function set60DayRange() {
    // Set start date to 60 days ago at 12:00:00 AM local time
    const start = dayjs().subtract(60, 'days').startOf('day')
    // Set end date to today at 11:59:59 PM local time
    const end = dayjs().endOf('day')
    
    startDate.value = start.format('YYYY-MM-DDTHH:mm:ssZ')
    endDate.value = end.format('YYYY-MM-DDTHH:mm:ssZ')
    isActive.value = true
  }

  // Set date range to last 90 days
  function set90DayRange() {
    // Set start date to 90 days ago at 12:00:00 AM local time
    const start = dayjs().subtract(90, 'days').startOf('day')
    // Set end date to today at 11:59:59 PM local time
    const end = dayjs().endOf('day')
    
    startDate.value = start.format('YYYY-MM-DDTHH:mm:ssZ')
    endDate.value = end.format('YYYY-MM-DDTHH:mm:ssZ')
    isActive.value = true
  }

  function resetStore() {
    resetDateRange()
  }

  storeRegistry.register('dateRange', resetStore)

  return {
    startDate,
    endDate,
    isActive,
    resetDateRange,
    setDateRange,
    toggleActive,
    getFormattedDates,
    set30DayRange,
    set60DayRange,
    set90DayRange,
    resetStore
  }
})
